import type { CursorWireState } from "@sc07-canvas/lib/net";

import { CanvasCore } from "../lib/canvas";
import { PaletteLib } from "../lib/palette";

const REGULAR_CURSOR_COLOR = "#ffffff";
const DARK_CURSOR_OUTLINE = "#111111";
const LIGHT_CURSOR_OUTLINE = "#ffffff";
const CURSOR_INTERPOLATION_DURATION = 70;
const PLACEMENT_ANIMATION_DURATION = 280;

interface RenderCursor {
  id: number;
  sequence: number;
  fromX: number;
  fromY: number;
  x: number;
  y: number;
  transitionStartedAt: number;
  zoom: number;
  color?: number;
  placementStartedAt?: number;
}

interface ReactionAnchor {
  element: HTMLElement;
  expiresAt: number;
}

export const colorToCss = (color: number | undefined) => {
  if (typeof color !== "number") return REGULAR_CURSOR_COLOR;

  const hex = PaletteLib.getColor(color)?.hex;
  if (!hex || hex === "transparent") return REGULAR_CURSOR_COLOR;
  return hex.startsWith("#") ? hex : `#${hex}`;
};

const contrastOutline = (color: string) => {
  const hex = color.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(hex)) return DARK_CURSOR_OUTLINE;

  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  const brightness = red * 0.299 + green * 0.587 + blue * 0.114;
  return brightness < 150 ? LIGHT_CURSOR_OUTLINE : DARK_CURSOR_OUTLINE;
};

export const outlineForColor = (color: number | undefined) =>
  contrastOutline(colorToCss(color));

const remoteDisplayScale = (remoteZoom: number, localZoom: number) => {
  const safeLocalZoom = Math.max(localZoom, 0.01);
  const safeRemoteZoom = Math.max(remoteZoom, 0.01);
  const sameLevelScale = Math.min(
    1,
    Math.max(0.5, Math.min(safeLocalZoom, safeRemoteZoom) / 2),
  );
  const ratio = safeRemoteZoom / safeLocalZoom;

  if (ratio >= 10) return { opacity: 0, scale: 0 };
  if (ratio >= 1) {
    return {
      opacity: 1,
      scale: sameLevelScale * (1 - (ratio - 1) / 9),
    };
  }

  const inverseRatio = 1 / ratio;
  if (inverseRatio >= 5) return { opacity: 0, scale: 0 };

  return {
    opacity: inverseRatio > 4 ? 5 - inverseRatio : 1,
    scale: sameLevelScale * (1 + (inverseRatio - 1) / 4),
  };
};

export class RemoteCursorRenderer {
  private readonly context: CanvasRenderingContext2D;
  private readonly cursors = new Map<number, RenderCursor>();
  private readonly reactions = new Map<number, ReactionAnchor>();
  private frame: number | undefined;
  private localZoom = 1;
  private disposed = false;

  constructor(
    private readonly surface: HTMLCanvasElement,
    private readonly canvas: CanvasCore,
  ) {
    const context = surface.getContext("2d");
    if (!context) throw new Error("Unable to create remote cursor renderer");
    this.context = context;
    this.resize();
  }

  apply(wire: CursorWireState) {
    const now = performance.now();
    const current = this.cursors.get(wire[0]);
    if (current && current.sequence > wire[1]) return;
    const position = current
      ? this.interpolatedPosition(current, now)
      : { x: wire[2], y: wire[3] };

    this.cursors.set(wire[0], {
      id: wire[0],
      sequence: wire[1],
      fromX: position.x,
      fromY: position.y,
      x: wire[2],
      y: wire[3],
      transitionStartedAt: now,
      zoom: wire[4],
      color: wire[5] ?? undefined,
      placementStartedAt: current?.placementStartedAt,
    });
    this.requestDraw();
  }

  remove(id: number) {
    this.cursors.delete(id);
    this.removeReaction(id);
    this.requestDraw();
  }

  retain(ids: Set<number>) {
    for (const id of this.cursors.keys()) {
      if (!ids.has(id)) this.remove(id);
    }
  }

  place(id: number) {
    const cursor = this.cursors.get(id);
    if (!cursor) return;
    cursor.placementStartedAt = performance.now();
    this.requestDraw();
  }

  attachReaction(id: number, element: HTMLElement, duration: number) {
    this.removeReaction(id);
    this.reactions.set(id, {
      element,
      expiresAt: performance.now() + duration,
    });
    this.requestDraw();
  }

  setLocalZoom(zoom: number) {
    this.localZoom = zoom;
    this.requestDraw();
  }

  resize() {
    const pixelRatio = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.surface.width = Math.max(1, Math.round(width * pixelRatio));
    this.surface.height = Math.max(1, Math.round(height * pixelRatio));
    this.surface.style.width = `${width}px`;
    this.surface.style.height = `${height}px`;
    this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    this.requestDraw();
  }

  requestDraw() {
    if (this.disposed || this.frame !== undefined) return;
    this.frame = requestAnimationFrame(this.draw);
  }

  clearReactions() {
    for (const id of [...this.reactions.keys()]) this.removeReaction(id);
  }

  clear() {
    this.cursors.clear();
    this.clearReactions();
    this.context.clearRect(0, 0, window.innerWidth, window.innerHeight);
  }

  dispose() {
    this.disposed = true;
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    this.frame = undefined;
    this.clear();
  }

  private draw = (now: number) => {
    this.frame = undefined;
    if (this.disposed) return;
    this.context.clearRect(0, 0, window.innerWidth, window.innerHeight);
    if (!this.canvas.isSurfaceReady()) return;
    let keepDrawing = false;

    for (const cursor of this.cursors.values()) {
      const position = this.interpolatedPosition(cursor, now);
      if (now - cursor.transitionStartedAt < CURSOR_INTERPOLATION_DURATION) {
        keepDrawing = true;
      }
      const placement = this.placementTransform(cursor, now);
      if (placement.active) keepDrawing = true;
      this.drawCursor(cursor, position.x, position.y, placement);
    }

    for (const [id, reaction] of this.reactions) {
      if (now >= reaction.expiresAt || !this.cursors.has(id)) {
        this.removeReaction(id);
        continue;
      }
      const cursor = this.cursors.get(id)!;
      const position = this.interpolatedPosition(cursor, now);
      const [screenX, screenY] = this.canvas.worldToScreen(
        position.x,
        position.y,
      );
      const display = remoteDisplayScale(cursor.zoom, this.localZoom);
      reaction.element.style.opacity = String(display.opacity);
      reaction.element.style.transform = `translate3d(${screenX}px, ${screenY}px, 0) scale(${display.scale})`;
      keepDrawing = true;
    }

    if (keepDrawing) this.requestDraw();
  };

  private drawCursor(
    cursor: RenderCursor,
    x: number,
    y: number,
    placement: { active: boolean; rotate: number; scale: number },
  ) {
    const [screenX, screenY] = this.canvas.worldToScreen(x, y);
    const display = remoteDisplayScale(cursor.zoom, this.localZoom);
    if (display.opacity <= 0 || display.scale <= 0) return;

    const context = this.context;
    context.save();
    context.globalAlpha = display.opacity;
    context.translate(screenX, screenY);
    context.rotate(placement.rotate);
    context.scale(
      display.scale * placement.scale,
      display.scale * placement.scale,
    );
    context.translate(-1, -1);
    context.beginPath();
    context.moveTo(1, 1);
    context.lineTo(1, 16);
    context.lineTo(5, 12);
    context.lineTo(8, 19);
    context.lineTo(10.5, 17.8);
    context.lineTo(7.5, 11.2);
    context.lineTo(13, 11.2);
    context.closePath();
    context.fillStyle = colorToCss(cursor.color);
    context.fill();
    context.strokeStyle = outlineForColor(cursor.color);
    context.lineWidth = 0.7;
    context.lineJoin = "round";
    context.stroke();
    context.restore();
  }

  private interpolatedPosition(cursor: RenderCursor, now: number) {
    const progress = Math.min(
      1,
      Math.max(
        0,
        (now - cursor.transitionStartedAt) / CURSOR_INTERPOLATION_DURATION,
      ),
    );
    return {
      x: cursor.fromX + (cursor.x - cursor.fromX) * progress,
      y: cursor.fromY + (cursor.y - cursor.fromY) * progress,
    };
  }

  private placementTransform(cursor: RenderCursor, now: number) {
    if (cursor.placementStartedAt === undefined) {
      return { active: false, rotate: 0, scale: 1 };
    }
    const progress =
      (now - cursor.placementStartedAt) / PLACEMENT_ANIMATION_DURATION;
    if (progress >= 1) {
      cursor.placementStartedAt = undefined;
      return { active: false, rotate: 0, scale: 1 };
    }
    const wave = Math.sin(Math.PI * Math.max(0, progress));
    return {
      active: true,
      rotate: (-14 * Math.PI * wave) / 180,
      scale: 1 - 0.1 * wave,
    };
  }

  private removeReaction(id: number) {
    const reaction = this.reactions.get(id);
    reaction?.element.remove();
    this.reactions.delete(id);
  }
}
